/**
 * Sale-line quantity helpers for Restaurant POS.
 *
 * Prevents the qty-reset-on-save bug where client +1 / hydrate paths lose
 * the operator's Qty and persist 1 instead.
 *
 * docs/FRONTEND.md — sale line Qty contract (PascalCase Qty/Unit)
 * Loss vectors:
 *  1. TablesSaleQty +1 skipped when instructions non-empty
 *  2. POS +1 via addToCart always commits Qty:1 instead of in-place bump
 *  3. Post-save / openTable hydrate loses qty→Qty casing
 *  4. addToCart merge key Unit.toFixed(2) splits otherwise-identical lines
 */

/**
 * Resolve a numeric quantity from mixed server/client casing.
 * Prefers PascalCase Qty (client cart contract), then lowercase variants.
 *
 * @param {Object|null|undefined} item - Sale line-like object
 * @returns {number} Finite quantity, or 0 when missing/invalid
 */
function resolveSaleLineQty(item) {
  if (!item || typeof item !== 'object') return 0;
  const raw = item.Qty ?? item.qty ?? item.Quantity ?? item.quantity ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Resolve a numeric unit price from mixed server/client casing.
 *
 * @param {Object|null|undefined} item - Sale line-like object
 * @returns {number} Finite unit price, or 0 when missing/invalid
 */
function resolveSaleLineUnit(item) {
  if (!item || typeof item !== 'object') return 0;
  const raw = item.Unit ?? item.unit ?? item.Price ?? item.price ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Normalize a single sale line so Qty and Unit are always PascalCase numbers.
 * Does not mutate the input; returns a shallow-copied line.
 *
 * @param {Object|null|undefined} item - Raw sale line from server or client
 * @returns {Object|null|undefined} Normalized line, or original non-object value
 */
function normalizeSaleLineFields(item) {
  if (!item || typeof item !== 'object') return item;
  const Qty = resolveSaleLineQty(item);
  const Unit = resolveSaleLineUnit(item);
  return {
    ...item,
    Qty,
    Unit,
  };
}

/**
 * Normalize an array of server/client sale lines for cart / table hydrate.
 * Ensures Qty/Unit casing, stable index, tourismLevy default, and savedQty.
 *
 * @param {Array|null|undefined} items - Raw items array
 * @returns {Array<Object>} Normalized lines (empty array when input invalid)
 */
function normalizeSaleLines(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => {
    const n = normalizeSaleLineFields(item) || {};
    const qty = resolveSaleLineQty(n);
    return {
      ...n,
      index: n.index != null ? n.index : index,
      tourismLevy: typeof n.tourismLevy === 'number' ? n.tourismLevy : 0,
      // savedQty tracks last-persisted qty for kitchen print deltas
      savedQty: Number.isFinite(Number(n.savedQty)) ? Number(n.savedQty) : qty,
      Qty: qty,
      Unit: resolveSaleLineUnit(n),
    };
  });
}

/**
 * Compare two Unit prices numerically without toFixed string drift.
 * e.g. 3.76289 and 3.76 should match within default epsilon when one side
 * was rounded for display; for exact identity use a tight epsilon.
 *
 * Default epsilon 1e-6 catches float noise; callers that intentionally
 * rounded with toFixed(2) should pass epsilon 0.005 so 3.76 ≈ 3.76289.
 *
 * @param {*} a - First unit value
 * @param {*} b - Second unit value
 * @param {number} [epsilon=1e-6] - Absolute tolerance
 * @returns {boolean} True when both finite and |a-b| < epsilon
 */
function unitsMatch(a, b, epsilon = 1e-6) {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return false;
  return Math.abs(na - nb) < epsilon;
}

/**
 * Merge-key unit match for addToCurrentSaleLines.
 * Tolerates display rounding (toFixed(2)) so 3.76289 and 3.76 merge
 * into one line instead of spawning a duplicate Qty:1 line.
 *
 * @param {*} a - First unit
 * @param {*} b - Second unit
 * @returns {boolean}
 */
function saleLineUnitsMatch(a, b) {
  // 0.005 ≈ half-cent of toFixed(2) rounding distance
  return unitsMatch(a, b, 0.005);
}

/**
 * Decide the outcome of a TablesSaleQty +/- press.
 * ALWAYS applies the qty change when newQty > 0 — instructions must not
 * suppress the bump (that was loss vector #1).
 *
 * @param {Object} saleLine - Current line ({ Qty, instructions? })
 * @param {number} change - +1 or -1
 * @returns {{ qty: number, action: 'void'|'update', viewInstructions: boolean }}
 */
function computeQuantityChange(saleLine, change) {
  const current = resolveSaleLineQty(saleLine);
  const delta = Number(change) || 0;
  const newQty = current + delta;
  const hasInstructions =
    Array.isArray(saleLine?.instructions) && saleLine.instructions.length >= 1;

  if (newQty <= 0) {
    return { qty: newQty, action: 'void', viewInstructions: false };
  }

  return {
    qty: newQty,
    action: 'update',
    // Separate UX path: surface instructions on +1 without blocking qty
    viewInstructions: delta === 1 && hasInstructions,
  };
}

/**
 * In-place +1 qty for an existing cart line (POS floating + / CurrentSaleQty).
 * Replaces the old addToCart(Qty:1) path that reset quantities on save.
 *
 * @param {Object} saleLine - Existing cart line with Qty
 * @returns {number} New quantity (current + 1)
 */
function nextInPlaceQty(saleLine) {
  return resolveSaleLineQty(saleLine) + 1;
}

module.exports = {
  resolveSaleLineQty,
  resolveSaleLineUnit,
  normalizeSaleLineFields,
  normalizeSaleLines,
  unitsMatch,
  saleLineUnitsMatch,
  computeQuantityChange,
  nextInPlaceQty,
};
